import React, { useState, useEffect } from 'react';
import { Typography, Table, Space, Tag, message, Drawer, Descriptions, Timeline, Spin, Button, DatePicker, Select, Grid, Card, List, Checkbox } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { congViecService } from '../services/congViecService';
import { masterDataService } from '../services/masterDataService';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

// Matches the CongViecDto from the backend
interface CongViecRecord {
    id: number;
    tieuDe: string;
    noiDung?: string;
    tenLoaiCongViec: string;
    tenNguoiTao: string;
    tenNguoiXuLyHienTai: string;
    tenKhoaPhong: string;
    ngayTao: string;
    ngayHetHan?: string;
    mucDoUuTien: number;
    trangThai: number;
}

interface CongViecLichSuRecord {
    id: number;
    tenNguoiThucHien: string;
    hanhDong: string;
    ghiChu?: string;
    thoiGianThucHien: string;
    duLieuThem?: string;
}

const QuanLyCongViecPage: React.FC = () => {
    const [tasks, setTasks] = useState<CongViecRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [isDetailVisible, setIsDetailVisible] = useState(false);
    const [selectedTask, setSelectedTask] = useState<CongViecRecord | null>(null);
    const [taskHistory, setTaskHistory] = useState<CongViecLichSuRecord[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Filter states
    const [fromDate, setFromDate] = useState<Dayjs | null>(null);
    const [toDate, setToDate] = useState<Dayjs | null>(null);
    const [taskTypeFilter, setTaskTypeFilter] = useState<number | undefined>(undefined);
    const [departmentFilter, setDepartmentFilter] = useState<number | undefined>(undefined);
    const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined);
    const [hideCancelled, setHideCancelled] = useState(true);
    const [hideCompleted, setHideCompleted] = useState(true);

    // Data for filters
    const [taskTypes, setTaskTypes] = useState<{ id: number, tenLoai: string }[]>([]);
    const [departments, setDepartments] = useState<{ makk: number, tenkk: string }[]>([]);

    const screens = useBreakpoint();

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                fromDate: fromDate?.toISOString(),
                toDate: toDate?.toISOString(),
                loaiCongViecId: taskTypeFilter,
                maKhoaPhong: departmentFilter,
                trangThai: statusFilter
            };
            const response = await congViecService.getFilteredTasks(params);
            setTasks(response.data);
        } catch (error) {
            console.error("Failed to fetch tasks:", error);
            message.error('Không thể tải danh sách công việc.');
        } finally {
            setLoading(false);
        }
    };

    const fetchFilterData = async () => {
        try {
            const [taskTypesRes, departmentsRes] = await Promise.all([
                congViecService.getAllLoaiCongViec(),
                masterDataService.getDepartments(),
            ]);
            setTaskTypes(taskTypesRes.data);
            setDepartments(departmentsRes.data);
        } catch (error) {
            message.error("Lỗi khi tải dữ liệu cho bộ lọc.");
        }
    };

    useEffect(() => {
        fetchData();
        fetchFilterData();
    }, []);

    const showDetailDrawer = async (record: CongViecRecord) => {
        setHistoryLoading(true);
        setSelectedTask(record);
        setIsDetailVisible(true);
        try {
            const historyResponse = await congViecService.getTaskHistory(record.id);
            setTaskHistory(historyResponse.data);
        } catch (error) {
            message.error('Không thể tải lịch sử công việc.');
        } finally {
            setHistoryLoading(false);
        }
    };

    const closeDetailDrawer = () => {
        setIsDetailVisible(false);
        setSelectedTask(null);
        setTaskHistory([]);
    };
    
    const priorityMap: { [key: number]: { color: string; text: string } } = {
        0: { color: 'default', text: 'Thấp' },
        1: { color: 'blue', text: 'Trung bình' },
        2: { color: 'red', text: 'Cao' },
    };

    const statusMap: { [key: number]: { color: string; text: string } } = {
        0: { color: 'blue', text: 'Mới' },
        1: { color: 'orange', text: 'Đang xử lý' },
        2: { color: 'green', text: 'Hoàn thành' },
        3: { color: 'default', text: 'Đã hủy' },
        4: { color: 'purple', text: 'Bị trả lại' },
    };

    // Helper function for deadline status styles
    const getDeadlineStatusStyles = (deadlineDate?: string, status?: number) => {
        let borderColor = 'transparent'; // Default
        let textColor = 'inherit';
        // Status 2: Hoàn thành, Status 3: Đã hủy
        const isDone = status === 2 || status === 3;

        if (deadlineDate && !isDone) {
            const deadline = dayjs(deadlineDate).startOf('day');
            const today = dayjs().startOf('day');
            
            if (today.isAfter(deadline)) {
                borderColor = '#ff4d4f'; // Ant Design Red for overdue
                textColor = '#ff4d4f';
            } else {
                const diffDays = deadline.diff(today, 'day');
                if (diffDays <= 1) { // Today or Tomorrow
                    borderColor = '#faad14'; // Ant Design Orange for nearing
                    textColor = '#faad14';
                } else if (diffDays <= 4) {
                    borderColor = '#fadb14'; // Ant Design Yellow for nearing
                    textColor = '#fadb14';
                }
            }
        }
        return { borderColor, textColor };
    };

    const columns: ColumnsType<CongViecRecord> = [
        { title: 'Tiêu Đề', dataIndex: 'tieuDe', key: 'tieuDe', fixed: 'left', width: 250 },
        { title: 'Ngày Tạo', dataIndex: 'ngayTao', key: 'ngayTao', render: (text) => dayjs(text).format('DD/MM/YYYY') },
        { title: 'Người Tạo', dataIndex: 'tenNguoiTao', key: 'tenNguoiTao' },
        { title: 'Người Đang Tiếp Nhận', dataIndex: 'tenNguoiXuLyHienTai', key: 'tenNguoiXuLyHienTai'},
        { title: 'Khoa/Phòng', dataIndex: 'tenKhoaPhong', key: 'tenKhoaPhong' },
        { title: 'Deadline', dataIndex: 'ngayHetHan', key: 'ngayHetHan', render: (text, record) => {
            if (!text) return 'N/A';
            const { textColor } = getDeadlineStatusStyles(record.ngayHetHan, record.trangThai);
            return (
                <span style={{ color: textColor }}>
                    {dayjs(text).format('DD/MM/YYYY')}
                </span>
            );
        }, sorter: (a, b) => dayjs(a.ngayHetHan).unix() - dayjs(b.ngayHetHan).unix() },
        { title: 'Ưu Tiên', dataIndex: 'mucDoUuTien', key: 'mucDoUuTien', render: (prio) => <Tag color={priorityMap[prio]?.color}>{priorityMap[prio]?.text}</Tag>, sorter: (a, b) => a.mucDoUuTien - b.mucDoUuTien },
        { title: 'Trạng Thái', dataIndex: 'trangThai', key: 'trangThai', render: (status) => <Tag color={statusMap[status]?.color}>{statusMap[status]?.text}</Tag> },
        { title: 'Hành Động', key: 'action', fixed: 'right', render: (_, record) => <Button type="link" onClick={() => showDetailDrawer(record)}>Xem chi tiết</Button> },
    ];
    
    const renderTaskList = () => {
    const filteredTasks = tasks.filter(task => {
        if (hideCancelled && task.trangThai === 3) return false;
        if (hideCompleted && task.trangThai === 2) return false;
        return true;
    }).sort((a, b) => dayjs(b.ngayTao).unix() - dayjs(a.ngayTao).unix()); // Sort by ngày tạo giảm dần

    const isMobile = screens.xs;

    if (isMobile) {
        return (
            <List
                grid={{ gutter: 16, xs: 1, sm: 2 }}
                dataSource={filteredTasks}
                loading={loading}
                renderItem={item => {
                    const { borderColor, textColor } = getDeadlineStatusStyles(item.ngayHetHan, item.trangThai);
                    let cardStyle: React.CSSProperties = { marginTop: 16, borderColor: borderColor };

                    return (
                         <List.Item>
                            <Card 
                                title={item.tieuDe} 
                                extra={<Button type="link" onClick={() => showDetailDrawer(item)}>Xem</Button>}
                                style={cardStyle}
                            >
                                <p><strong>Người tạo:</strong> {item.tenNguoiTao}</p>
                                <p><strong>Người xử lý:</strong> {item.tenNguoiXuLyHienTai}</p>
                                <p><strong>Khoa/Phòng:</strong> {item.tenKhoaPhong}</p>
                                <p><strong>Deadline: </strong> 
                                    <span style={{ color: textColor }}>
                                        {item.ngayHetHan ? dayjs(item.ngayHetHan).format('DD/MM/YYYY') : 'N/A'}
                                    </span>
                                </p>
                                <p><strong>Trạng thái:</strong> <Tag color={statusMap[item.trangThai]?.color}>{statusMap[item.trangThai]?.text}</Tag></p>
                                <p><strong>Ưu tiên:</strong> <Tag color={priorityMap[item.mucDoUuTien]?.color}>{priorityMap[item.mucDoUuTien]?.text}</Tag></p>
                            </Card>
                        </List.Item>
                    )
                }}
            />
        );
    }

    return (
        <Table
            columns={columns}
            dataSource={filteredTasks}
            loading={loading}
            rowKey="id"
            scroll={{ x: 'max-content' }}
        />
    );
};


    return (
        <div style={{ padding: '24px' }}>
            <Title level={4}>Quản lý công việc</Title>
            <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                <DatePicker value={fromDate} onChange={setFromDate} placeholder="Từ ngày" />
                <DatePicker value={toDate} onChange={setToDate} placeholder="Đến ngày" />
                <Select allowClear placeholder="Lọc theo loại CV" style={{ width: 200 }} onChange={setTaskTypeFilter} value={taskTypeFilter}>
                    {taskTypes.map(t => <Option key={t.id} value={t.id}>{t.tenLoai}</Option>)}
                </Select>
                <Select allowClear placeholder="Lọc theo khoa phòng" style={{ width: 200 }} onChange={setDepartmentFilter} value={departmentFilter}>
                     {departments.map(d => <Option key={d.makk} value={d.makk}>{d.tenkk}</Option>)}
                </Select>
                <Select allowClear placeholder="Lọc theo trạng thái" style={{ width: 150 }} onChange={setStatusFilter} value={statusFilter}>
                    {Object.entries(statusMap).map(([key, val]) => <Option key={key} value={parseInt(key, 10)}>{val.text}</Option>)}
                </Select>
                <Checkbox checked={hideCancelled} onChange={e => setHideCancelled(e.target.checked)}>Ẩn Đã Hủy</Checkbox>
                <Checkbox checked={hideCompleted} onChange={e => setHideCompleted(e.target.checked)}>Ẩn Đã Hoàn Thành</Checkbox>
                <Button type="primary" onClick={fetchData} loading={loading}>Lọc</Button>
            </Space>
            
            {renderTaskList()}

            <Drawer
                title="Chi tiết công việc"
                width={!screens.xs ? 720 : '90%'}
                onClose={closeDetailDrawer}
                open={isDetailVisible}
                destroyOnClose
            >
                {selectedTask && (
                    <Spin spinning={historyLoading}>
                        <Descriptions bordered column={1} size="small">
                            <Descriptions.Item label="Tiêu đề">{selectedTask.tieuDe}</Descriptions.Item>
                            <Descriptions.Item label="Người tạo">{selectedTask.tenNguoiTao}</Descriptions.Item>
                            <Descriptions.Item label="Người xử lý hiện tại">{selectedTask.tenNguoiXuLyHienTai}</Descriptions.Item>
                            <Descriptions.Item label="Khoa/Phòng">{selectedTask.tenKhoaPhong}</Descriptions.Item>
                            <Descriptions.Item label="Ngày tạo">{dayjs(selectedTask.ngayTao).format('HH:mm DD/MM/YYYY')}</Descriptions.Item>
                            <Descriptions.Item label="Deadline">{selectedTask.ngayHetHan ? dayjs(selectedTask.ngayHetHan).format('DD/MM/YYYY') : 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Nội dung">{selectedTask.noiDung || 'Không có'}</Descriptions.Item>
                        </Descriptions>

                        <Title level={5} style={{ marginTop: 24 }}>Lưu đồ xử lý</Title>
                        <Timeline>
                            {taskHistory.map(item => (
                                <Timeline.Item key={item.id}>
                                    <p><Text strong>{item.hanhDong}</Text> bởi <Text strong>{item.tenNguoiThucHien}</Text></p>
                                    <p>{dayjs(item.thoiGianThucHien).format('HH:mm DD/MM/YYYY')}</p>
                                    {item.ghiChu && <Card size="small" type="inner" style={{background: '#f5f5f5'}}>Ghi chú: {item.ghiChu}</Card>}
                                </Timeline.Item>
                            ))}
                        </Timeline>
                    </Spin>
                )}
            </Drawer>
        </div>
    );
};

export default QuanLyCongViecPage;
